import {
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Skeleton,
  Typography,
} from "antd";
import { useState } from "react";
import ChangeLabel from "./ChangeLabel";
import FilterBar from "./FilterBar";
import KpiCard from "./KpiCard";
import { changeColor, describeChange, lastYearOnlyRanges } from "./deltaFormat";

const { Text } = Typography;

const rateKpis = [
  {
    key: "conversionRate",
    title: "Bind Rate",
    tooltip:
      "Bound ÷ Received — the percentage of received applications that result in a bound policy.",
    isPercentage: true,
  },
  {
    key: "closeRatio",
    title: "Close Ratio",
    tooltip:
      "Bound ÷ Quoted — the percentage of quoted applications that are ultimately bound.",
    isPercentage: true,
  },
  {
    key: "quoteRate",
    title: "Quote Rate",
    tooltip:
      "Quoted ÷ Received — the percentage of received applications for which a quote has been issued (awaiting answer, declined, or bound).",
    isPercentage: true,
  },
  {
    key: "declineRate",
    title: "Decline Rate",
    tooltip:
      "Not Bound ÷ Quoted — the percentage of quoted applications where the quote was declined (marked as not bound).",
    isPercentage: true,
    invertDelta: true,
  },
];

const detailItems = [
  { key: "applicationsReceived", label: "Applications Received" },
  { key: "quotesIssued", label: "Quotes Issued" },
  { key: "boundPolicies", label: "Bound Policies" },
  { key: "quotesDeclined", label: "Quotes Declined", invertDelta: true },
];

const statusColor = {
  strong: "#389e0d",
  neutral: "#8c8c8c",
  poor: "#cf1322",
};

const InlineDelta = ({ item, comparison, invertDelta }) => {
  if (!item) return "—";

  const compared = item.comparisons?.[comparison];
  const valueColor = statusColor[compared?.status] || "rgba(0,0,0,0.85)";
  const change = describeChange({ delta: compared?.delta, isPercentage: false });

  return (
    <span>
      <Text strong style={{ color: valueColor }}>
        {item.value}
      </Text>
      {change && (
        <span
          className="inline-delta"
          style={{ color: changeColor(change.direction, invertDelta) }}
        >
          <ChangeLabel
            change={change}
            comparison={comparison}
            isPercentage={false}
            value={item.value}
            earlierValue={compared.value}
          />
        </span>
      )}
    </span>
  );
};

const OverviewSection = ({ overviewByTimeRange, loading }) => {
  const [timeRange, setTimeRange] = useState("30d");
  const [type, setType] = useState("all");
  const [comparison, setComparison] = useState("lastYear");

  const changeTimeRange = (range) => {
    setTimeRange(range);
    if (lastYearOnlyRanges.includes(range)) setComparison("lastYear");
  };

  const rangeKey = timeRange === "YTD" ? "ytd" : timeRange;
  const block = overviewByTimeRange?.[rangeKey]?.[type];

  return (
    <Card
      className="body-card-content-no-min-height"
      title="Conversion Rate Metrics"
    >
      <>
        <FilterBar
          timeRange={timeRange}
          type={type}
          comparison={comparison}
          onTimeRangeChange={changeTimeRange}
          onTypeChange={setType}
          onComparisonChange={setComparison}
        />

        {!loading && !block ? (
          <Empty description="No data for selected filters" />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                <Row gutter={[12, 12]}>
                  {rateKpis.map((kpi) => (
                    <Col xs={12} xl={12} xxl={6} key={kpi.key}>
                      <KpiCard
                        title={kpi.title}
                        kpi={block?.[kpi.key]}
                        comparison={comparison}
                        isPercentage={kpi.isPercentage}
                        loading={loading}
                        tooltip={kpi.tooltip}
                        invertDelta={kpi.invertDelta}
                      />
                    </Col>
                  ))}
                </Row>
              </Col>

              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                {/* Details card */}
                <>
                  {loading ? (
                    <Skeleton active paragraph={{ rows: 4 }} title={false} />
                  ) : (
                    <>
                      <Descriptions
                        column={1}
                        size="small"
                        className="kpi-descriptions"
                        bordered
                        styles={{ label: { width: 160 } }}
                      >
                        {detailItems.map((d) => (
                          <Descriptions.Item key={d.key} label={d.label}>
                            <InlineDelta
                              item={block?.[d.key]}
                              comparison={comparison}
                              invertDelta={d.invertDelta}
                            />
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    </>
                  )}
                </>
              </Col>
            </Row>
          </>
        )}
      </>
    </Card>
  );
};

export default OverviewSection;
